import { useCallback, useState } from "react";

import type { CreateDatabaseRequest, ProvisionModeUI } from "../../../types";

export interface DatabaseFormState {
  name: string;
  mode: ProvisionModeUI;
  haReplicas: "1+0" | "1+1" | "1+2" | "1+3";
  storageSizeGi: number;
  postgresVersion: "15" | "16" | "17";
  extensions: string[];
}

export const INITIAL_DB_STATE: DatabaseFormState = {
  name: "",
  mode: "shared",
  haReplicas: "1+1",
  storageSizeGi: 10,
  postgresVersion: "16",
  extensions: [],
};

export interface DatabaseFormErrors {
  name?: string;
}

const POSTGRES_IDENTIFIER_RE = /^[a-zA-Z_][a-zA-Z0-9_]*$/;

export function useDatabaseForm(projectAlias: string) {
  const [state, setState] = useState<DatabaseFormState>(INITIAL_DB_STATE);
  const [errors, setErrors] = useState<DatabaseFormErrors>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  const validateField = useCallback((field: string, value: string) => {
    if (field === "name") {
      if (!value.trim()) return "Database name is required";
      if (!POSTGRES_IDENTIFIER_RE.test(value))
        return "Must start with a letter or underscore, only alphanumeric and underscore";
      if (value.length > 50) return "Name must be 50 characters or less";
    }
    return undefined;
  }, []);

  const setField = useCallback(
    <K extends keyof DatabaseFormState>(
      field: K,
      value: DatabaseFormState[K],
    ) => {
      setState((s) => ({ ...s, [field]: value }));
      if (touched[field as string]) {
        const err = validateField(field as string, String(value));
        setErrors((e) => ({ ...e, [field as string]: err }));
      }
    },
    [touched, validateField],
  );

  const onBlur = useCallback(
    (field: string) => {
      setTouched((t) => ({ ...t, [field]: true }));
      const err = validateField(
        field,
        String(state[field as keyof DatabaseFormState] ?? ""),
      );
      setErrors((e) => ({ ...e, [field]: err }));
    },
    [state, validateField],
  );

  const validate = useCallback((): boolean => {
    const newErrors: DatabaseFormErrors = {};
    const nameErr = validateField("name", state.name);
    if (nameErr) newErrors.name = nameErr;
    setErrors(newErrors);
    setTouched({ name: true });
    return Object.keys(newErrors).length === 0;
  }, [state, validateField]);

  const buildRequest = useCallback((): CreateDatabaseRequest => {
    const req: CreateDatabaseRequest = {
      name: state.name,
      mode: state.mode,
    };
    if (state.mode === "dedicated") {
      req.haReplicas = state.haReplicas;
      req.storageSizeGi = state.storageSizeGi;
      req.postgresVersion = state.postgresVersion;
      if (state.extensions.length > 0) req.extensions = state.extensions;
    }
    return req;
  }, [state]);

  return {
    state,
    errors,
    setField,
    onBlur,
    validate,
    buildRequest,
    projectAlias,
  };
}
